import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type {
  Project,
  RequirementType,
  Workspace,
  WorkspaceVersion,
  Paginated,
  TestCasePriority,
  TestCaseStatusEntity,
  User,
} from "@/common/types/generated";
import { TableConfig } from "@/config/appConfig";

interface QueryParams {
  versionId: number;

  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

const _buildQueryParams = (params: QueryParams): string => {
  const {
    versionId,

    page = 0,
    size = TableConfig.ITEMS_PER_PAGE,
    query = "",
    sort = "name,asc",
  } = params;

  const searchParams = new URLSearchParams({
    versionId: String(versionId),

    page: String(page),
    size: String(size),
    query,
    sort,
  });

  return `?${searchParams.toString()}`;
};

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const buildSpecialQueryParams = (params: GenericQueryParams): string => {
  const { query, ...rest } = params;

  const searchParams = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value));
    }
  });
  const queryPart = query ? `query=${query}` : "";
  return `?${[queryPart, searchParams.toString()].filter(Boolean).join("&")}`;
};

export const createproject = (payload: Partial<Project>) => {
  return httpClient<void>(
    httpMethods.POST,
    API_ENDPOINTS.WORKSPACE.project,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
};
export const fetchProjectById = (id: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.project}/${id}`;
  return httpClient<Project>(httpMethods.GET, endpoint);
};

// --- Project Summary (aggregated workspace counts) ---
//
// Backed by `GET /project/:id/summary` — see
// `backend/src/controllers/core/projectSummaryController.ts` for the
// authoritative `ProjectSummaryResponse` shape this maps onto.
export interface ProjectSummaryWorkspace {
  id: string;
  name: string;
  versionCount: number;
  latestVersion: { id: string; name: string; createdAt: string } | null;
  testCycleCount: number;
  testCaseCount: number;
  lastRunAt: string | null;
}

export interface ProjectSummary {
  projectId: string;
  projectName: string;
  workspaces: ProjectSummaryWorkspace[];
}

export const fetchProjectSummary = (id: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.project}/${id}/summary`;
  return httpClient<ProjectSummary>(httpMethods.GET, endpoint);
};

// --- Project Overview (rich dashboard aggregation) ---
//
// Backed by `GET /project/:id/overview` — see
// `backend/src/controllers/core/projectOverviewController.ts` for the
// authoritative `ProjectOverviewResponse` shape this maps onto.
export type ProjectPlatform = "Web" | "Mobile" | "API" | "Desktop";
export type HealthLevel = "healthy" | "at-risk" | "critical";
export type MetricTone = "success" | "warning" | "info" | "destructive";

export interface ProjectOverview {
  project: {
    id: number;
    key: string;
    name: string;
    website: string | null;
    description: string | null;
    organization: string | null;
    velocityWindow: string;
    velocityWindowKey: string | null;
    version: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    owner: string | null;
    ownerInitials: string | null;
    ownerEmail: string | null;
    lead: string | null;
    manager: string | null;
    platform: ProjectPlatform;
    type: string;
    status: "Active" | "Archived";
    industry: string | null;
    strategy: string | null;
    framework: string | null;
  };
  health: {
    score: number;
    level: HealthLevel;
    signals: {
      coverage: number;
      automation: number;
      passRate: number;
      flakyPct: number;
      defects: number;
      requirementsCoverage: number;
    };
  };
  kpis: {
    workspaces: number;
    members: number;
    testCases: number;
    requirements: number;
    cycles: number;
    releases: number;
    automationPct: number;
    coveragePct: number;
    passRatePct: number;
    defects: number;
    velocityPerDay: number;
  };
  deltas: {
    testCases: number | null;
    requirements: number | null;
    defects: number | null;
  };
  workspaces: Array<{
    id: number;
    name: string;
    platform: ProjectPlatform;
    type: string;
    active: boolean;
    lead: string | null;
    cases: number;
    requirements: number;
    cycles: number;
    releases: number;
    coverage: number;
    automation: number;
    passRate: number;
    integrations: string[];
    tags: string[];
  }>;
  members: Array<{
    userId: number;
    name: string;
    email: string | null;
    role: string;
    initials: string;
    assigned: number;
    active: number;
    velocity: number;
    capacity: number;
    lastActive: string | null;
    risk: boolean;
  }>;
  activity: Array<{
    actor: string;
    event: string;
    target: string;
    when: string;
    tone: "success" | "info" | "warning" | "destructive" | null;
  }>;
  environments: Array<{
    name: string;
    pct: number;
    tone: "success" | "warning" | "destructive";
  }>;
  topFailingModule: {
    name: string;
    passRate: number;
    failingCases: number;
    cycles: number;
  } | null;
  missingToday: {
    unassignedCases: number;
    uncoveredRequirements: number;
    releasesWithoutSignoff: number;
  };
  ai: {
    lastSync: string | null;
    status: string;
    partial?: boolean;
    confidence: number | null;
    coverage: number | null;
    suggestions: number | null;
  };
}

export const fetchProjectOverview = (id: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.project}/${id}/overview`;
  return httpClient<ProjectOverview>(httpMethods.GET, endpoint);
};

export const updateproject = (id: number, payload: Partial<Project>) => {
  return httpClient<void>(
    httpMethods.PUT,
    `${API_ENDPOINTS.WORKSPACE.project}/${id}`,
    { ...payload, id }, // ✅ send id in JSON body too
  );
};
export const deleteprojectbyid = (id: number) => {
  return httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.WORKSPACE.project}/${id}`,
    { id }, // ✅ send id in JSON body too
  );
};
export const changepassword = (payload: Record<string, unknown>) => {
  const endpoint = `${API_ENDPOINTS.AUTH.changepassword}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const updatetestcasepriorityById = (
  typeId: number,
  payload: Partial<TestCasePriority>,
) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasepriorities}${typeId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};
export const createtestcasepriority = (payload: Partial<TestCasePriority>) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasepriorities}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const deletetestcaseprioritiesbyId = (testcasepriorirtid: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasepriorities}${testcasepriorirtid}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const updatetestcasestatusById = (
  statusId: number,
  payload: Partial<TestCaseStatusEntity>,
) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasestatuses}${statusId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};
export const createtestcasestatus = (
  payload: Partial<TestCaseStatusEntity>,
) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasestatuses}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};
export const deletetestcasestatusbyId = (statusId: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.testcasestatuses}${statusId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const updaterequirementtypeById = (
  typeId: number,
  payload: Partial<RequirementType>,
) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}${typeId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};
export const createrequirementtype = (payload: Partial<RequirementType>) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const deleterequirementtypebyId = (requirementtypeid: number) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}${requirementtypeid}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const fetchRequirementTypes = (projectId: number) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}?query=projectId:${projectId}`;
  return httpClient<Paginated<RequirementType>>(httpMethods.GET, endpoint);
};

export const fetchRequirementTypeById = (requirementtypeid: number) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}${requirementtypeid}`;
  return httpClient<RequirementType>(httpMethods.GET, endpoint);
};

export const createworkspaceversion = (payload: Partial<WorkspaceVersion>) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.workspaceVersions}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};
export const createworkspace = (payload: Partial<Workspace>) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.workspace}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

// --- Workspace (Application) CRUD ---
export const fetchWorkspacesByProjectId = (projectId: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.workspace}projectid/${projectId}`;
  return httpClient<Workspace[]>(httpMethods.GET, endpoint);
};
export const updateWorkspace = (id: number, payload: Partial<Workspace>) => {
  return httpClient<void>(
    httpMethods.PUT,
    `${API_ENDPOINTS.WORKSPACE.workspace}${id}`,
    payload,
  );
};
export const deleteWorkspace = (id: number) => {
  return httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.WORKSPACE.workspace}${id}`,
  );
};

// --- Workspace Version CRUD ---
export const fetchVersionsByWorkspaceId = (workspaceId: number) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.version}${workspaceId}`;
  return httpClient<WorkspaceVersion[]>(httpMethods.GET, endpoint);
};
export const updateVersion = (
  id: number,
  payload: Partial<WorkspaceVersion>,
) => {
  return httpClient<void>(
    httpMethods.PUT,
    `${API_ENDPOINTS.WORKSPACE.workspaceVersions}${id}`,
    payload,
  );
};
export const deleteVersion = (id: number) => {
  return httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.WORKSPACE.workspaceVersions}${id}`,
  );
};
export const cloneVersion = (payload: Record<string, unknown>) => {
  return httpClient<void>(
    httpMethods.POST,
    `${API_ENDPOINTS.WORKSPACE.workspaceVersions}clone`,
    payload,
  );
};

// --- Project Members ---
export const updateUserById = (id: number, payload: Partial<User>) => {
  return httpClient<void>(
    httpMethods.PATCH,
    `${API_ENDPOINTS.AUTH.user}${id}`,
    payload,
  );
};
export const deleteUserById = (id: number) => {
  return httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.AUTH.user}${id}`,
  );
};
